import * as React from 'react';

import classNames from 'classnames';
import { compose } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import type { TypographyProps } from '@coursera/cds-core';
import type { ClickButton } from '@coursera/event-pulse-types';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import ShareCTADefault from 'bundles/sharing-common/components/modal/buttons/ShareCTADefault';
import { NATIVE_MOBILE_SOURCE_PARAM, SHARE_QUERY_PARAM } from 'bundles/sharing-common/constants';
import type { SocialCaptions } from 'bundles/sharing-common/types/sharingSharedTypes';
import withCustomUrl from 'bundles/sharing-common/utils/withCustomUrl';
import withFormattedShareLink from 'bundles/sharing-common/utils/withFormattedShareLink';

const TABLET_SCREEN_PX = 768;

export type DownloadHandler = {
  downloadFile: (url?: string, filename?: string) => Promise<void>;
  isDownloading: boolean;
};

export type PropsFromCaller = {
  rootClassName?: string;
  style?: React.CSSProperties;
  // Link to be shared, if undefined will use current page
  shareLink?: string;
  utmMediumParam?: string;
  utmContentParam?: string;
  utmCampaignParam?: string;
  utmProductParam?: string;
  // provide an object of keys to string or number pairings and they will be added as query params
  extraQueryParams?: {
    [key: string]: string | number;
  };
  // To use a custom CTA or button wrap this component around it
  children?: React.JSX.Element;
  // Custom messages to add to social channels, check SocialCaptions type for more info, only some platforms enable this feature
  captions?: SocialCaptions;
  // Turn your shareLink into a short link (formatted like /share/:hash), not ready to be enabled yet for all pages.
  // The URL displayed in the Copy Link container is shortened by default.
  useCustomUrl?: boolean;
  // By default, the Copy Link container enables URL shortening. To disable, set this flag.
  disableCopyLinkCustomUrl?: boolean;
  // Title for modal
  title?: string;
  // Description for modal
  description?: string;
  disableDescription?: boolean;
  onSocialButtonClick?: (event: React.MouseEvent<HTMLElement>) => void;
  // Pass in a component to show a preview of the share, resides below title/description and above share links
  previewComponent?: React.JSX.Element;
  // For video sharing, provide a getter for current time in seconds to enable a start at checkbox.
  // If this is enabled it disables custom URLs (for now).
  getVideoTimeInSeconds?: () => number;
  // Title component for modal
  titleComponent?: TypographyProps['component'];
  // Whether to make tabbable. Should depend on what we want screen readers to announce; this or the child element (usually button)
  tabbable?: boolean;
  // URL for the file download button. When present, a download button will be shown in the modal.
  downloadUrl?: string;
  // When present, will be used as the suggested filename for the download. Note that some browsers will ignore this.
  downloadName?: string;
  downloadHandler?: DownloadHandler;
  dispatchClickEvents?: boolean;
  additionalEventingData?: Omit<ClickButton, 'button' | 'page'>;
};

type PropsToComponent = PropsFromCaller & {
  // For native mobile share link
  // targetShareLink is the shareLink with proper UTM params added, customShareLink is the short link version
  targetShareLink: string;
  customShareLink?: string;
};

type State = {
  showModal: boolean;
};

const LoadableShareModal = createLoadableComponent(() => import('bundles/sharing-common/components/modal/ShareModal'));

export class ShareButtonWithModal extends React.Component<PropsToComponent, State> {
  state = {
    showModal: false,
  };

  componentDidMount() {
    if (window?.location?.search.includes(SHARE_QUERY_PARAM)) {
      this.setState(() => ({
        showModal: true,
      }));
    }
  }

  handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    const { onSocialButtonClick } = this.props;

    if (onSocialButtonClick) {
      onSocialButtonClick(event);
    }

    // @todo: Extract the native mobile check to a sharable util, to be used in some use cases where ShareModal is used alone.
    // If screen size is in tablet range or below and native sharing is enabled use that
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share, available and best for mobile devices
    if (window.innerWidth <= TABLET_SCREEN_PX && window.navigator && 'share' in navigator) {
      this.nativeMobileShare();
    } else {
      this.setState(() => ({
        showModal: true,
      }));
    }
  };

  handleCloseModal = () => {
    this.setState(() => ({
      showModal: false,
    }));
  };

  // @todo: Extract the function to a sharable util, to be used in some use cases where ShareModal is used alone.
  nativeMobileShare = () => {
    const { captions, targetShareLink, customShareLink } = this.props;
    const link = customShareLink || targetShareLink;

    const title = captions?.nativeMobileTitle?.(link);
    const caption = captions?.nativeMobileMessage?.(link);

    navigator
      .share({
        url: customShareLink || targetShareLink,
        title: title || document.title,
        text: caption || '',
      })
      .catch((err) => {
        // Swallow the error if the user aborted the share, or if the device has no share targets.
        if (err.name !== 'AbortError') {
          throw err;
        }
      });
  };

  render() {
    const {
      rootClassName,
      style,
      shareLink,
      captions,
      utmContentParam,
      utmMediumParam,
      utmCampaignParam,
      utmProductParam,
      children,
      useCustomUrl,
      title,
      description,
      disableDescription,
      previewComponent,
      extraQueryParams,
      getVideoTimeInSeconds,
      titleComponent,
      tabbable,
      downloadUrl,
      downloadName,
      downloadHandler,
      dispatchClickEvents,
      additionalEventingData,
    } = this.props;
    const { showModal } = this.state;
    const rootClassNames = classNames('rc-ShareButtonWithModal', rootClassName);

    return (
      <div className={rootClassNames} style={style} data-e2e="universal-share-cta">
        <TrackedDiv
          aria-label={title}
          trackingName="universal_sharing_cta"
          onClick={this.handleButtonClick}
          tabIndex={tabbable ? 0 : -1}
          trackMouseEnters
        >
          {children || <ShareCTADefault />}
        </TrackedDiv>
        {showModal && (
          <LoadableShareModal
            dispatchClickEvents={dispatchClickEvents}
            additionalEventingData={additionalEventingData}
            handleCloseModal={this.handleCloseModal}
            shareLink={shareLink}
            captions={captions}
            utmContentParam={utmContentParam}
            utmMediumParam={utmMediumParam}
            utmCampaignParam={utmCampaignParam}
            utmProductParam={utmProductParam}
            extraQueryParams={extraQueryParams}
            useCustomUrl={useCustomUrl}
            title={title}
            description={description}
            disableDescription={disableDescription}
            previewComponent={previewComponent}
            initialVideoTimeInSeconds={getVideoTimeInSeconds && getVideoTimeInSeconds()}
            titleComponent={titleComponent}
            downloadUrl={downloadUrl}
            downloadName={downloadName}
            downloadHandler={downloadHandler}
          />
        )}
      </div>
    );
  }
}

/**
 * Renders a share button that opens a modal for sharing options. On mobile devices, it may use native sharing functionality.
 */
const ShareButtonWithModalWithData = compose<PropsToComponent, PropsFromCaller>(
  // The source is mobile as the link is only used by the above mobile native case.
  // For the other scenarios, the link will be generated within each of the share button with the corresponding source.
  withFormattedShareLink(NATIVE_MOBILE_SOURCE_PARAM),
  withCustomUrl()
)(ShareButtonWithModal);

const ShareButtonWithModalWithErrorBoundary = (props: PropsFromCaller) => (
  <ErrorBoundaryWithTagging componentName="ShareButtonWithModal">
    <ShareButtonWithModalWithData {...props} />
  </ErrorBoundaryWithTagging>
);

export default ShareButtonWithModalWithErrorBoundary;
